import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { default as classNamesFN } from 'classnames';
import { useCallback, useMemo } from 'react';
import { DayButton, DayPicker, DayPickerProps, Months, MonthsProps, useDayPicker } from 'react-day-picker';
import { SwipeableProps, useSwipeable } from 'react-swipeable';

import { cn } from '@/lib/css-utils';

import { defaultBaseUIComponentProps } from './base';
import { IconButton, buttonTextVariants, buttonVariants } from './button';
import { Paragraph } from './paragraph';
import { useGlobalDefaults, useParentMode } from './provider';

export type CalendarProps = DayPickerProps;

const MonthsWrapperComponent = ({ ...props }: MonthsProps) => {
    const dayPickerCtx = useDayPicker();

    const nextMonth = dayPickerCtx.nextMonth;
    const prevMonth = dayPickerCtx.previousMonth;
    const goToMonth = dayPickerCtx.goToMonth;

    const onSwipedRight = useCallback(() => {
        if (prevMonth) {
            goToMonth(prevMonth);
        }
    }, [prevMonth, goToMonth]);

    const onSwipedLeft = useCallback(() => {
        if (nextMonth) {
            goToMonth(nextMonth);
        }
    }, [nextMonth, goToMonth]);

    const config: SwipeableProps = useMemo(
        () => ({
            onSwipedLeft,
            onSwipedRight,
        }),
        [onSwipedLeft, onSwipedRight]
    );

    const handlers = useSwipeable(config);

    return (
        <div {...handlers}>
            <Months {...props} />
        </div>
    );
};

export const Calendar = ({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) => {
    const defaults = useGlobalDefaults(defaultBaseUIComponentProps);
    const parentMode = useParentMode();
    const mode = parentMode ?? defaults.mode;

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            {...props}
            weekStartsOn={1}
            className={cn(
                'p-4 border-token-stroke-input-focused border rounded-lg inline-flex bg-token-bg-dp01 touch-none',
                className
            )}
            classNames={{
                months: 'inline-flex flex-col sm:flex-row space-y-4 sm:space-y-0 relative',
                month: '',
                month_caption: 'flex justify-center pt-1 items-center',
                caption_label: 'leading-[21px]',
                nav: 'flex items-center absolute left-0 right-0 top-px',
                nav_button: 'relative',
                month_grid: 'w-full border-collapse mt-3.5 -mx-2',
                weekdays: 'flex pb-1.5',
                weekday: 'w-9 leading-4.5',
                week: 'flex w-full mt-0.5',
                day: classNamesFN(
                    'w-9 flex justify-center',
                    'relative focus-within:relative focus-within:z-20',
                    '[&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent'
                ),
                day_button: classNamesFN(
                    'border-transparent hover:border-transparent focus:border-transparent focus:outline-none',
                    buttonTextVariants({ variant: 'text' }),
                    'disabled:border-transparent',
                    'px-2 py-0.5'
                ),
                range_end: 'day-range-end',
                selected: '',
                today: '',
                focused: '',
                outside: 'day-outside',
                disabled: 'text-muted-foreground opacity-50 border-transparent',
                range_middle: '',
                hidden: 'invisible',
                ...classNames,
            }}
            components={{
                NextMonthButton: ({ ...props }) => <IconButton icon={IconChevronRight} {...props} />,
                PreviousMonthButton: ({ ...props }) => (
                    <>
                        <IconButton icon={IconChevronLeft} {...props} />
                        <div className="grow" />
                    </>
                ),

                DayButton: ({ className, children, modifiers, ...props }) => {
                    return (
                        <DayButton
                            className={cn(
                                buttonVariants({
                                    variant: 'icon',
                                    size: 'md',
                                }),
                                className
                            )}
                            modifiers={modifiers}
                            {...props}
                        >
                            <Paragraph
                                mode={mode}
                                className={classNamesFN(
                                    buttonTextVariants({
                                        mode,
                                        variant: 'icon',
                                        disabled: props.disabled,
                                        className: classNamesFN({
                                            'text-token-text-light-disabled': !!modifiers.outside,
                                            'text-nInfo-500': !!modifiers.today,
                                            'text-token-text-light-hover font-medium underline': !!modifiers.selected,
                                            'text-token-text-light-focused font-medium': modifiers.focused,
                                        }),
                                        size: 'sm',
                                        isLoading: false,
                                    }),
                                    'text-xs'
                                )}
                                as="span"
                            >
                                {children}
                            </Paragraph>
                        </DayButton>
                    );
                },

                CaptionLabel: ({ className, ...props }) => (
                    <Paragraph className={className} {...props} fontWeight="medium">
                        {props.children}
                    </Paragraph>
                ),

                Weekday: ({ className, ...props }) => (
                    <Paragraph className={className} {...props} as="th" size="xxs">
                        {props.children}
                    </Paragraph>
                ),

                Months: MonthsWrapperComponent,
            }}
        />
    );
};
